import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, XCircle, ArrowRight, RotateCcw, Trophy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Question {
  id: string;
  question: string;
  options: string[];
  correct_answer: number;
  user_answer: number | null;
}

interface QuizViewerProps {
  setId: string;
  title: string;
  onClose: () => void;
}

export const QuizViewer = ({ setId, title, onClose }: QuizViewerProps) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadQuestions = async () => {
      const { data, error } = await supabase
        .from("quiz_questions")
        .select("*")
        .eq("set_id", setId)
        .order("created_at");

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load quiz questions",
          variant: "destructive",
        });
        return;
      }

      // Parse options from JSONB
      const parsedQuestions = data.map((q) => ({
        ...q,
        options: Array.isArray(q.options) ? q.options : JSON.parse(q.options as string),
      }));

      setQuestions(parsedQuestions);
      setIsLoading(false);
    };

    loadQuestions();
  }, [setId, toast]);

  const handleSelectAnswer = (answerIndex: number) => {
    if (isAnswered) return;
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null) return;

    setIsAnswered(true);
    const isCorrect = selectedAnswer === questions[currentIndex].correct_answer;
    if (isCorrect) {
      setScore((prev) => prev + 1);
    }

    // Save user's answer to database
    await supabase
      .from("quiz_questions")
      .update({ user_answer: selectedAnswer })
      .eq("id", questions[currentIndex].id);

    // Update local state
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === currentIndex ? { ...q, user_answer: selectedAnswer } : q
      )
    );
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    } else {
      setIsComplete(true);
    }
  };

  const handleRetry = async () => {
    // Reset all user answers in database
    await supabase
      .from("quiz_questions")
      .update({ user_answer: null })
      .eq("set_id", setId);

    // Reset local state
    setQuestions((prev) => prev.map((q) => ({ ...q, user_answer: null })));
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setScore(0);
    setIsComplete(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-muted-foreground">No questions found in this quiz.</p>
        <Button onClick={onClose} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  if (isComplete) {
    const percentage = Math.round((score / questions.length) * 100);
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="text-center space-y-4">
          <Trophy className="w-16 h-16 mx-auto text-yellow-500" />
          <h2 className="text-2xl font-bold">Quiz Complete!</h2>
          <div className="text-4xl font-bold text-primary">
            {score}/{questions.length}
          </div>
          <p className="text-muted-foreground">
            You scored {percentage}%
          </p>
          <Progress value={percentage} className="w-full max-w-xs mx-auto h-3" />
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold text-lg">Review Answers:</h3>
          {questions.map((q, index) => (
            <Card
              key={q.id}
              className={`${
                q.user_answer === q.correct_answer
                  ? "border-green-500/50 bg-green-500/5"
                  : "border-red-500/50 bg-red-500/5"
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {q.user_answer === q.correct_answer ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                  )}
                  <div className="space-y-1">
                    <p className="font-medium">Q{index + 1}: {q.question}</p>
                    <p className="text-sm text-green-600">
                      Correct: {q.options[q.correct_answer]}
                    </p>
                    {q.user_answer !== q.correct_answer && (
                      <p className="text-sm text-red-600">
                        Your answer: {q.options[q.user_answer!]}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={handleRetry}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Retry Quiz
          </Button>
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Question {currentIndex + 1} of {questions.length}</span>
          <span>Score: {score}</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <Card className="animate-fade-in">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-6">{currentQuestion.question}</h3>
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => {
              let buttonClass = "w-full justify-start text-left h-auto py-3 px-4";
              
              if (isAnswered) {
                if (index === currentQuestion.correct_answer) {
                  buttonClass += " bg-green-500/20 border-green-500 hover:bg-green-500/20";
                } else if (index === selectedAnswer && index !== currentQuestion.correct_answer) {
                  buttonClass += " bg-red-500/20 border-red-500 hover:bg-red-500/20";
                }
              } else if (index === selectedAnswer) {
                buttonClass += " bg-primary/20 border-primary";
              }

              return (
                <Button
                  key={index}
                  variant="outline"
                  className={buttonClass}
                  onClick={() => handleSelectAnswer(index)}
                  disabled={isAnswered}
                >
                  <span className="font-semibold mr-3">
                    {String.fromCharCode(65 + index)}.
                  </span>
                  <span className="break-words">{option}</span>
                  {isAnswered && index === currentQuestion.correct_answer && (
                    <CheckCircle2 className="w-5 h-5 ml-auto text-green-500 shrink-0" />
                  )}
                  {isAnswered && index === selectedAnswer && index !== currentQuestion.correct_answer && (
                    <XCircle className="w-5 h-5 ml-auto text-red-500 shrink-0" />
                  )}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onClose}>
          Exit Quiz
        </Button>
        {!isAnswered ? (
          <Button onClick={handleSubmitAnswer} disabled={selectedAnswer === null}>
            Submit Answer
          </Button>
        ) : (
          <Button onClick={handleNext}>
            {currentIndex < questions.length - 1 ? (
              <>
                Next Question
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            ) : (
              "View Results"
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
